import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useStore } from '../lib/store'
import { api } from '../lib/api'

const SPECIALTIES = [
  'Family Medicine','Internal Medicine','Pediatrics','General Surgery',
  'Orthopedic Surgery','Spine Surgery','Neurosurgery','Cardiothoracic Surgery',
  'Urology','Cardiology','Pulmonology / Critical Care','Gastroenterology',
  'Neurology','Dermatology','Emergency Medicine','Hospital Medicine / Hospitalist',
  'Anesthesiology','Psychiatry','ENT / Otolaryngology','Ophthalmology','OB/GYN',
  'Radiology','Pathology','Other',
]
const EMR_SYSTEMS = [
  'Epic','Cerner / Oracle Health','athenahealth','eClinicalWorks','ModMed (Modernizing Medicine)',
  'NextGen Healthcare','DrChrono','Allscripts / Veradigm','Practice Fusion','AdvancedMD',
  'Kareo / Tebra','Other EHR','Paper Charts','None',
]
type PracticeSize = 'solo' | '2-5' | '6-20' | '20+'

export function OAuthOnboardingPage() {
  const navigate = useNavigate()
  const { updateDoctor } = useStore()
  const [specialty, setSpecialty] = useState('')
  const [practiceSize, setPracticeSize] = useState<PracticeSize | ''>('')
  const [currentEmr, setCurrentEmr] = useState('')
  const [agreeTerms, setAgreeTerms] = useState(false)
  const [agreePrivacy, setAgreePrivacy] = useState(false)
  const [agreeBaa, setAgreeBaa] = useState(false)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  const appName = 'Dr. Scribey'

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError('')
    if (!specialty) return setError('Please select your specialty')
    if (!practiceSize) return setError('Please select your practice size')
    if (!currentEmr) return setError('Please select your EMR')
    if (!agreeTerms || !agreePrivacy || !agreeBaa) return setError('You must agree to all terms, privacy policy, and BAA')
    setLoading(true)
    try {
      const updatedDoctor = await api.completeOnboarding({
        specialty,
        practice_size: practiceSize,
        current_emr: currentEmr,
        agree_terms: agreeTerms,
        agree_privacy: agreePrivacy,
        agree_baa: agreeBaa,
      })
      updateDoctor(updatedDoctor)
      navigate('/welcome')
    } catch (err: any) {
      setError(err.message || 'Something went wrong')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-8">
      <div className="w-full max-w-xl">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-brand-500">{appName}</h1>
          <p className="text-gray-500 dark:text-gray-400 mt-2">One more step — complete your profile</p>
        </div>
        <form onSubmit={handleSubmit} className="bg-gray-100 dark:bg-gray-900 rounded-xl p-6 space-y-4 shadow-xl border border-gray-200 dark:border-gray-800">
          {error && <div className="bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-800 text-red-600 dark:text-red-300 text-sm rounded-lg p-3">{error}</div>}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Specialty <span className="text-red-500">*</span></label>
            <select value={specialty} onChange={e => setSpecialty(e.target.value)} className="w-full bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg px-3 py-2 text-sm text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-brand-500" required>
              <option value="">Select your specialty</option>
              {SPECIALTIES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Practice Size <span className="text-red-500">*</span></label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[{value:'solo',label:'Solo'},{value:'2-5',label:'2–5'},{value:'6-20',label:'6–20'},{value:'20+',label:'20+'}].map(o => (
                <label key={o.value} className={`flex items-center justify-center px-4 py-3 rounded-lg border-2 cursor-pointer transition-colors ${practiceSize===o.value?'border-brand-500 bg-brand-100 dark:bg-brand-500/10 text-brand-400':'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-300'}`}>
                  <input type="radio" name="ps" value={o.value} checked={practiceSize===o.value} onChange={e=>setPracticeSize(e.target.value as PracticeSize)} className="sr-only" />
                  <span className="text-sm font-medium">{o.label}</span>
                </label>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Current EMR <span className="text-red-500">*</span></label>
            <select value={currentEmr} onChange={e => setCurrentEmr(e.target.value)} className="w-full bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg px-3 py-2 text-sm text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-brand-500" required>
              <option value="">Select your EMR</option>
              {EMR_SYSTEMS.map(emr => <option key={emr} value={emr}>{emr}</option>)}
            </select>
          </div>
          <div className="border-t border-gray-200 dark:border-gray-800 pt-4 space-y-3">
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Legal Agreements</p>
            {[
              {key:'terms', checked:agreeTerms, set:setAgreeTerms, label:'Terms of Service', href:'/terms'},
              {key:'privacy', checked:agreePrivacy, set:setAgreePrivacy, label:'Privacy Policy', href:'/privacy'},
              {key:'baa', checked:agreeBaa, set:setAgreeBaa, label:'Business Associate Agreement (BAA)', href:'/baa'},
            ].map(item => (
              <label key={item.key} className="flex items-start gap-3 cursor-pointer">
                <input type="checkbox" checked={item.checked} onChange={e=>item.set(e.target.checked)} className="mt-0.5 w-5 h-5 rounded border-gray-300 dark:border-gray-700 focus:ring-brand-500 checked:bg-brand-600" required />
                <span className="text-sm text-gray-700 dark:text-gray-300">I agree to the <a href={item.href} target="_blank" className="text-brand-400 hover:text-brand-300 underline">{item.label}</a> <span className="text-red-500">*</span></span>
              </label>
            ))}
          </div>
          <button type="submit" disabled={loading} className="w-full bg-brand-600 hover:bg-brand-500 disabled:bg-brand-800 text-white font-medium py-3 rounded-lg transition-colors mt-2">
            {loading ? 'Saving...' : 'Complete Setup'}
          </button>
        </form>
      </div>
    </div>
  )
}
